<template>
  <div>
    <div v-if="!mappings.length" class="empty-mapping">
      <i class="bx bx-transfer" style="font-size: 32px; color: #b8bdd0;"></i>
      <p>Nenhum campo mapeado.</p>
    </div>

    <table v-else class="mapping-table">
      <thead>
        <tr>
          <th style="width: 32px;"></th>
          <th>Campo de Origem</th>
          <th>{{ fixedTarget ? 'Campo no Destino' : 'Coluna na Planilha' }}</th>
          <th style="width: 36px;"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, index) in mappings" :key="index">
          <td>
            <span class="mapping-drag">
              <i class="bx bx-grid-vertical" style="font-size: 16px; color: #b8bdd0;"></i>
            </span>
          </td>
          <td>
            <select
              class="form-select"
              v-model="row.selectedField"
              @change="onFieldSelect(index, row.selectedField)"
            >
              <option value="">-- Selecione --</option>
              <optgroup
                v-for="cat in availableFields"
                :key="cat.category"
                :label="cat.label"
              >
                <option
                  v-for="field in cat.fields"
                  :key="field.source_type + ':' + field.source_key"
                  :value="field.source_type + ':' + field.source_key"
                >
                  {{ field.label }}
                </option>
              </optgroup>
            </select>
          </td>
          <td>
            <!-- Destino fixo (select dos campos do serviço) -->
            <select
              v-if="fixedTarget"
              class="form-select"
              v-model="row.target_column"
              @change="emitUpdate"
            >
              <option value="">-- Selecione --</option>
              <option
                v-for="t in (targetFields || [])"
                :key="t.key"
                :value="t.key"
              >
                {{ t.label }}
              </option>
            </select>
            <!-- Destino livre (nome da coluna na planilha) -->
            <input
              v-else
              type="text"
              class="form-input"
              v-model="row.target_column"
              placeholder="Ex: NOME"
              @input="emitUpdate"
            />
          </td>
          <td>
            <button class="mapping-remove" @click="removeRow(index)" title="Remover">
              <i class="bx bx-x" style="font-size: 18px;"></i>
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <button class="add-mapping-btn" @click="addRow">
      <i class="bx bx-plus-circle" style="font-size: 15px;"></i>
      Adicionar campo
    </button>
  </div>
</template>

<script>
export default {
  name: 'FieldMappingTable',
  props: {
    value: {
      type: Array,
      default: () => []
    },
    availableFields: {
      type: Array,
      default: () => []
    },
    // Lista fixa de campos de destino [{key, label}] — quando presente, o
    // destino vira um <select> em vez de texto livre.
    targetFields: {
      type: Array,
      default: null
    },
    fixedTarget: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      mappings: []
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (val) {
        if (val && val.length) {
          this.mappings = val.map(m => ({
            ...m,
            selectedField: m.source_type && m.source_key ? `${m.source_type}:${m.source_key}` : ''
          }))
        } else {
          this.mappings = []
        }
      }
    }
  },
  methods: {
    findField (sourceType, sourceKey) {
      for (const cat of this.availableFields) {
        const found = cat.fields.find(
          f => f.source_type === sourceType && f.source_key === sourceKey
        )
        if (found) return found
      }
      return null
    },
    addRow () {
      this.mappings.push({
        source_type: '',
        source_key: '',
        target_column: '',
        selectedField: '',
        sequence: this.mappings.length
      })
    },
    removeRow (index) {
      this.mappings.splice(index, 1)
      this.emitUpdate()
    },
    onFieldSelect (index, val) {
      if (val) {
        const [sourceType, ...rest] = val.split(':')
        const sourceKey = rest.join(':')
        this.mappings[index].source_type = sourceType
        this.mappings[index].source_key = sourceKey

        if (!this.mappings[index].target_column) {
          if (this.fixedTarget) {
            // Destino fixo: tenta casar automaticamente pela chave de origem
            // (ex: source 'email' \u2192 target 'email'). Sen\u00e3o, deixa o usu\u00e1rio escolher.
            const match = (this.targetFields || []).find(t => t.key === sourceKey)
            if (match) {
              this.mappings[index].target_column = match.key
            }
          } else {
            const field = this.findField(sourceType, sourceKey)
            if (field) {
              this.mappings[index].target_column = field.label
                .toUpperCase()
                .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
                .replace(/[^A-Z0-9]+/g, '_')
                .replace(/^_|_$/g, '')
                .substring(0, 30)
            }
          }
        }
      } else {
        this.mappings[index].source_type = ''
        this.mappings[index].source_key = ''
      }
      this.emitUpdate()
    },
    emitUpdate () {
      const output = this.mappings.map((m, i) => ({
        source_type: m.source_type,
        source_key: m.source_key,
        target_column: m.target_column,
        sequence: i
      }))
      this.$emit('input', output)
    }
  }
}
</script>

<style scoped>
.mapping-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid #e2e5ed;
  border-radius: 6px;
  overflow: hidden;
}
.mapping-table th {
  background: #f8f9fb;
  padding: 9px 14px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #8d92a9;
  text-align: left;
  border-bottom: 1px solid #e2e5ed;
}
.mapping-table td {
  padding: 6px 14px;
  border-bottom: 1px solid #edf0f5;
  vertical-align: middle;
}
.mapping-table tr:last-child td { border-bottom: none; }

.mapping-table .form-select,
.mapping-table .form-input {
  width: 100%;
  padding: 7px 10px;
  font-size: 12px;
  border: 1px solid #e2e5ed;
  border-radius: 6px;
  font-family: inherit;
  color: #1e2235;
  background: #fff;
  transition: all 0.15s;
  outline: none;
}
.mapping-table .form-select:focus,
.mapping-table .form-input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px #eff4ff;
}
.mapping-table .form-input::placeholder { color: #b8bdd0; }
.mapping-table .form-select optgroup { font-weight: 600; color: #1e2235; }
.mapping-table .form-select option { font-weight: 400; color: #4d5269; }

.mapping-drag {
  color: #b8bdd0;
  cursor: grab;
  display: flex;
  align-items: center;
}
.mapping-remove {
  color: #b8bdd0;
  cursor: pointer;
  background: none;
  border: none;
  padding: 4px;
  transition: color 0.15s;
  display: flex;
}
.mapping-remove:hover { color: #f46a6a; }

.add-mapping-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 10px;
  font-size: 12px;
  font-weight: 500;
  color: #2563eb;
  cursor: pointer;
  background: none;
  border: none;
  font-family: inherit;
}
.add-mapping-btn:hover { opacity: 0.7; }

.empty-mapping {
  text-align: center;
  padding: 28px 0;
  color: #8d92a9;
}
.empty-mapping p {
  margin-top: 8px;
  font-size: 13px;
}
</style>
